import type { FilterCardConfig } from '@framework/analytics/models/card-model';
import {
	CubeFilterOperator,
	type PageCardFilterInput,
} from '@framework/analytics/models/page-filter.model';
import { canonicalDataSourceMetaKey } from '@jepto/shared/utils/data-source-icon-meta.util';

export interface ResolveFilterCardDataSourceContext {
	member: string;
	cardId?: string;
}

/**
 * Resolves Cube data source for a filter card from `config.sources[0].dataSource`.
 * Member prefixes are not inferred — page filters apply broadly via `getPageData`.
 */
export function resolveFilterCardDataSource(
	config: Pick<FilterCardConfig, 'sources'> | undefined,
	context: ResolveFilterCardDataSourceContext
): string {
	const fromSources = config?.sources?.[0]?.dataSource;
	if (fromSources && typeof fromSources === 'string') {
		return fromSources;
	}

	const fromMember = inferFilterCardDataSourceFromMember(context.member);
	if (fromMember) {
		return fromMember;
	}

	const cardPart = context.cardId ? `cardId=${context.cardId}, ` : '';
	throw new Error(
		`Filter card dataSource is required (${cardPart}member=${context.member}). Set sources[0].dataSource on the filter card config.`
	);
}

/** When `sources[]` is missing, infer connector key from dimension member prefix. */
export function inferFilterCardDataSourceFromMember(
	member: string
): string | undefined {
	const trimmed = member?.trim() ?? '';
	const dot = trimmed.indexOf('.');
	if (dot <= 0) {
		return undefined;
	}
	return canonicalDataSourceMetaKey(trimmed.slice(0, dot));
}

/** Reads the shared search term from stashed `contains` page filters for one card. */
export function extractSearchQueryFromStashedFilters(
	filters: PageCardFilterInput[]
): string {
	const values = filters
		.filter(filter => filter.operator === CubeFilterOperator.Contains)
		.map(filter => filter.values[0]?.trim())
		.filter((value): value is string => !!value);

	if (values.length === 0) {
		return '';
	}

	return values[0];
}

/** Builds `pageFilters` entries for a search filter card query (one per configured dimension). */
export function buildSearchPageFilters(
	config: Pick<FilterCardConfig, 'data' | 'sources'>,
	cardId: string,
	query: string
): PageCardFilterInput[] {
	const trimmed = query.trim();
	if (!trimmed) {
		return [];
	}

	const members = (config.data?.dimensions ?? [])
		.map(dimension => dimension.name?.trim())
		.filter((name): name is string => !!name);

	if (members.length === 0) {
		return [];
	}

	return members.map(member => ({
		cardId,
		member,
		operator: CubeFilterOperator.Contains,
		values: [trimmed],
		dataSource: resolveFilterCardDataSource(config, { member, cardId }),
	}));
}
